import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { easeInOut } from '@config/animations.config';
import { IAccessoriesCarouselCard } from '@models/IAccessoriesCarouselCard';
import AccessoryTypeEnum from '@models/enums/AccessoryTypeEnum';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';

interface Props extends IAccessoriesCarouselCard {
  readonly accessoryType?: AccessoryTypeEnum;
  readonly dataTestId: string;
}

const AccessoriesCarouselCard = ({
  name,
  thumbnail,
  buyNowLink,
  learnMoreLink,
  trackingData,
  accessoryType,
  dataTestId,
}: Props) => {
  const [isHovered, setHover] = useState(false);
  const [ctaBoxHeight, setCtaBoxHeight] = useState(0);

  const ctaBoxRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setCtaBoxHeight(node.clientHeight ?? 0);
    }
  }, []);

  const isBelowLargeDesktop = useMediaQuery(1440);

  const ctaVariants = {
    hover: { opacity: 1 },
    initial: { opacity: isBelowLargeDesktop ? 1 : 0 },
  };

  const nameVariants = {
    hover: { y: -ctaBoxHeight },
    initial: { y: 0 },
  };

  const imgVariants = {
    hover: { y: -13, scale: 1.1 },
    initial: { y: 0, scale: 1 },
  };

  return (
    <div
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      track-product-impression="true"
      data-product={JSON.stringify(trackingData ?? '')}
      className="pointer-events-auto relative flex h-full w-full flex-col justify-between gap-5 overflow-hidden rounded-[10px] bg-grey-100 px-7 py-8 lg:py-10 dark:bg-grey-600"
      data-testid={dataTestId}
    >
      {thumbnail && (
        <motion.div
          initial="initial"
          animate={isHovered && !isBelowLargeDesktop ? 'hover' : 'initial'}
          variants={imgVariants}
          transition={easeInOut}
        >
          {learnMoreLink?.url || buyNowLink?.url ? (
            <a
              href={learnMoreLink?.url || buyNowLink?.url}
              target={learnMoreLink?.target || buyNowLink?.target}
              aria-label={name}
              className="flex h-full w-full"
              data-testid={`${dataTestId}-Link`}
            >
              <Image
                image={thumbnail}
                classes="w-full h-full"
                imageClasses="w-full h-full object-contain max-h-[200px] pb-5"
                dataTestId={`${dataTestId}-Image`}
              />
            </a>
          ) : (
            <Image
              image={thumbnail}
              imageClasses="w-full h-full object-contain max-h-[200px] pb-5"
              dataTestId={`${dataTestId}-Image`}
            />
          )}
        </motion.div>
      )}

      <motion.div
        initial="initial"
        animate={isHovered && !isBelowLargeDesktop ? 'hover' : 'initial'}
        variants={nameVariants}
        transition={easeInOut}
        className="relative flex w-full flex-col items-center justify-center text-center"
      >
        {name && (
          <p
            className="body-one text-center text-black-100 dark:text-white"
            data-testid={`${dataTestId}-Name`}
          >
            {name}
          </p>
        )}

        {buyNowLink?.url && buyNowLink?.name && (
          <div className="top-full flex w-full justify-center pt-5 xl:absolute">
            <motion.div
              initial="initial"
              animate={isHovered && !isBelowLargeDesktop ? 'hover' : 'initial'}
              transition={{ duration: 0.2 }}
              variants={ctaVariants}
              ref={ctaBoxRef}
            >
              <Button
                link={buyNowLink}
                linkCallback={() => {
                  if (!trackingData) return;

                  const contentType = (() => {
                    if (accessoryType === AccessoryTypeEnum.IncludedAccessories)
                      return 'Included in the box';
                    if (accessoryType === AccessoryTypeEnum.OptionalAccessories)
                      return 'Optional Accessories';
                    return null;
                  })();

                  updateDataLayer({
                    event: 'selectItem',
                    ...(contentType ? { content_type: contentType } : {}),
                    ecommerce: {
                      currency: trackingData.currency,
                      items: [mapToNewTrackingData(trackingData)],
                    },
                  });
                }}
                data-testid={`${dataTestId}-Buy-Now-CTA`}
              />
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AccessoriesCarouselCard;
